import React, { useEffect, useState } from 'react';
import FormFieldV2 from '@/components/common/form/FormFieldV2';
import { FIELD_TYPE } from '@/constants/form';
import { useAppDispatch } from '@/redux';
import { addApiErrorToast } from '@/redux/slices/toast.slice';
import { AppFullLocale, ValueLabel } from '@/types';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { formatCountriesList, getSubLocale } from '@/utils/locales';
import { SelectFieldV2Props } from '../SelectFieldV2';
import { TextFieldV2Props } from '../TextFieldV2';

type CountryData = {
	favorites: ValueLabel[];
	others: ValueLabel[];
	selectedCountry: ValueLabel;
};
type CountrySelectorProps = {
	locale: AppFullLocale;
	country?: string;
	isFromExpro?: boolean;
} & Partial<SelectFieldV2Props> &
	TextFieldV2Props;

const CountrySelectorV2 = (props: CountrySelectorProps): React.JSX.Element => {
	const { locale, label, country, name, isFromExpro, ...rest } = props;
	const [countryData, setCountryData] = useState<CountryData | undefined>();
	const dispatch = useAppDispatch();

	useEffect(() => {
		const fetchCountries = async () => {
			let favorites: ValueLabel[] = [];
			let others: ValueLabel[] = [];
			let selectedCountry: ValueLabel;

			const countryCode = country || getSubLocale(locale);
			try {
				[favorites, others, selectedCountry] = await formatCountriesList(locale, countryCode);
			} catch (error) {
				dispatch(addApiErrorToast({ error }));
			}

			others = others.filter((val) => !isObjectEmpty(val));
			favorites = favorites.filter((val) => !isObjectEmpty(val));

			if (isFromExpro) {
				others = favorites.concat(others).filter((val) => {
					if (countryCode === 'FR') {
						return val.value === countryCode || val.value === 'MC';
					}
					return val.value === countryCode;
				});
				favorites = [];
			}

			setCountryData({
				favorites,
				others,
				selectedCountry,
			});
		};

		fetchCountries();
	}, [locale]);

	return countryData ? (
		<FormFieldV2
			type={FIELD_TYPE.SELECT}
			label={label}
			name={name}
			options={countryData?.others}
			favorites={countryData?.favorites}
			defaultValue={countryData?.selectedCountry?.value}
			disabled={isFromExpro && countryData?.others?.length < 2}
			{...rest}
		/>
	) : null;
};

export default CountrySelectorV2;
